/**
 * 飞书 Webview 通信（与 pe-npds-fe-house / service-track / 工单 webviewManager 一致）
 * 依赖飞书 JSSDK（index.html 已引入），在飞书小程序 web-view 内通过 tt.miniProgram.createMessageManager().transferMessage 与基座通信
 * - 发请求带 _id + callback，基座回包时按 _id 执行对应 callback（请求-响应）
 * - 主应用收到基座消息后同时透传给 FeishuBridgeUtils.subscribeFeishuBaseMessage 的订阅者
 */

const FEISHU_MANAGER_KEY = '_FEISHU-WEBVIEW-MANAGER_'

/** 基座下发的消息结构（与 onTransferMessage 回调 res 一致） */
export type FeishuBaseMessagePayload = { data?: { _id?: number; action?: string; data?: unknown } }

interface MessagePayload {
  action: string
  [key: string]: unknown
}

type MessageManager = {
  transferMessage: (opts: {
    data: MessagePayload & { _id?: number; data?: unknown }
    success?: (res: unknown) => void
    fail?: (res: unknown) => void
  }) => void
  onTransferMessage: (callback: (res: FeishuBaseMessagePayload) => void) => void
}

const baseMessageListeners = new Set<(res: FeishuBaseMessagePayload) => void>()
const callbackMap: Record<number, (data: unknown) => void> = {}

function handleBaseMessage(res: FeishuBaseMessagePayload): void {
  console.log('handleBaseMessage', res)
  const payload = res?.data ?? (res as unknown as { _id?: number; data?: unknown })
  const _id = payload?._id
  const responseData = payload?.data

  if (_id !== undefined && _id !== null && callbackMap[_id]) {
    const fn = callbackMap[_id]
    delete callbackMap[_id]
    try {
      fn(responseData)
    } catch (err) {
      console.error('[feishuBridge] 基座回包回调执行异常', err)
    }
  }

  console.log('[feishuBridge] 收到基座消息', res)
  baseMessageListeners.forEach((cb) => {
    try {
      cb(res)
    } catch (err) {
      console.error('[feishuBridge] 基座消息订阅回调执行异常', err)
    }
  })
}

export function getMessageManager(): MessageManager | null {
  try {
    const win = window as unknown as Record<string, unknown> & {
      tt: { miniProgram: { createMessageManager: () => MessageManager } }
    }
    let manager: MessageManager = (win[FEISHU_MANAGER_KEY] as MessageManager)
    if (manager) return manager
    manager = win.tt.miniProgram.createMessageManager()
    console.log('manager', manager)
    if (manager) {
      win[FEISHU_MANAGER_KEY] = manager
      manager.onTransferMessage((res) => {
        handleBaseMessage(res)
      })
    }
    return manager
  } catch {
    console.error('[feishuBridge] 获取 MessageManager 失败')
    return null
  }
}

/**
 * 供子应用通过 getRootProps() 获取，用于订阅基座下发的消息（主应用收到后透传）
 */
export const FeishuBridgeUtils = {
  /**
   * 订阅基座消息，主应用收到基座消息后会调用 callback
   * @returns 取消订阅的函数
   */
  subscribeFeishuBaseMessage(callback: (res: FeishuBaseMessagePayload) => void): () => void {
    baseMessageListeners.add(callback)
    return () => {
      baseMessageListeners.delete(callback)
    }
  }
}

/**
 * 向飞书基座发送消息（如 docsPicker、openSchema、openLocation 等）
 * 若传 callback，则按 _id 请求-响应：基座回包后执行 callback(回包 data)；未在飞书 webview 或发送失败时也会调用 callback 传错参
 */
export function handleTransferMessage(
  data: MessagePayload,
  callback?: (result: { success: boolean; res?: unknown } | unknown) => void
): void {
  const h5Manager = getMessageManager()
  if (!h5Manager) {
    console.warn('[feishuBridge] tt.miniProgram 不可用，请确保在飞书 webview 内打开')
    callback?.({ success: false, res: { errMsg: 'bridge unavailable' } })
    return
  }

  const { action, ...rest } = data
  let payload: Record<string, unknown> = { action, ...rest }
  let pendingId: number | undefined

  if (typeof callback === 'function') {
    pendingId = Math.random()
    callbackMap[pendingId] = (responseData: unknown) => {
      callback(responseData)
    }
    payload = { _id: pendingId, action, data: rest }
  }

  h5Manager.transferMessage({
    data: payload as MessagePayload & { _id?: number; data?: unknown },
    success: (res) => {
      if (typeof callback === 'function' && pendingId === undefined) {
        callback({ success: true, res })
      }
    },
    fail: (res: unknown) => {
      if (pendingId !== undefined) {
        delete callbackMap[pendingId]
      }
      if (typeof callback === 'function') {
        callback({ success: false, res })
      }
    },
  })
}

/** 是否在飞书 webview 内（有 bridge 可用） */
export function isFeishuWebview(): boolean {
  return getMessageManager() !== null
}

/** 主应用启动时调用，提前创建并挂到 window，子应用 mfe-utils 通过 getGlobalWindow()[KEY] 复用同一 manager 才能收到基座回包 */
export function initFeishuBridgeManager(): void {
  if (isFeishuWebview()) getMessageManager()
}
